import React from "react";
import {
  Icon,
  IconButton,
  TableCell,
  Tooltip,
  Typography,
} from "@material-ui/core";
import MapIcon from "@material-ui/icons/Map";
import EventNoteIcon from "@material-ui/icons/EventNote";
import { makeStyles } from "@material-ui/core/styles";
import { getTextColor } from "../../utils";
import { NO_PRINT_CLASS } from "../../pages/wqScorecard/scorecardPrint";
import {
  OPEN_IN_MAP_HINT,
  SEASON_DETAIL,
} from "../../pages/wqScorecard/scorecardCopy";

const useStyles = makeStyles((theme) => ({
  cell: {
    textAlign: "center",
    padding: theme.spacing(0.5, 0.5),
    borderLeft: "1px solid rgba(224, 224, 224, 1)",
    position: "relative",
  },
  // A thicker rule between the regulatory columns and the rest.
  groupDivider: {
    borderLeft: "3px solid rgba(0, 0, 0, 0.35)",
  },
  value: {
    fontWeight: 600,
    fontSize: "0.88rem",
    lineHeight: 1.15,
    display: "block",
  },
  // A number from fewer than six samples is shown, but shown as provisional:
  // lighter and italic, with its count beneath. The arrow is left alone,
  // because the trend is tested over its own, longer window.
  valueMuted: {
    fontWeight: 500,
    fontStyle: "italic",
    opacity: 0.72,
  },
  trendRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 1,
  },
  trendIcon: {
    fontSize: "1.05rem",
  },
  smallSample: {
    display: "block",
    fontSize: "0.6rem",
    fontStyle: "italic",
    marginTop: 2,
    opacity: 0.85,
  },
  noData: {
    fontStyle: "italic",
    opacity: 0.8,
  },
  // Never sampled here, or not a column for this site: a quiet dash, so the
  // eye skips it instead of reading another n/a.
  dash: {
    opacity: 0.35,
  },
  // Always rendered rather than revealed on hover: an affordance that only
  // appears on hover is invisible to keyboard and touch users. Subtle at rest,
  // solid on hover or focus.
  mapButton: {
    padding: 1,
    marginTop: 0,
    color: "inherit",
    opacity: 0.45,
    transition: theme.transitions.create(["opacity", "transform"], {
      duration: theme.transitions.duration.shortest,
    }),
    "&:hover, &:focus": {
      opacity: 1,
      transform: "scale(1.15)",
      backgroundColor: "rgba(0, 0, 0, 0.08)",
    },
    "&:focus": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  mapIcon: {
    fontSize: "1rem",
  },
  tooltipTitle: {
    fontWeight: 600,
    marginBottom: 4,
  },
  tooltipLine: {
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
  // The label half of a "Label: value" line, so the eye can find a field in
  // a dozen lines without reading each one.
  tooltipKey: {
    fontWeight: 600,
    opacity: 0.85,
  },
  tooltipHint: {
    fontSize: "0.72rem",
    lineHeight: 1.4,
    marginTop: 6,
    fontStyle: "italic",
    opacity: 0.9,
  },
  srOnly: {
    position: "absolute",
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
}));

// A tooltip line is "Label: value" when it is a field, or a sentence when it
// is a note. Only the former gets a bold label: a short key with no sentence
// punctuation before the first colon.
const MAX_KEY_LENGTH = 40;
export function splitTooltipLine(line) {
  const index = String(line).indexOf(": ");
  if (index <= 0 || index > MAX_KEY_LENGTH) return [null, line];
  const key = String(line).slice(0, index);
  if (/[.!?]/.test(key)) return [null, line];
  return [key, String(line).slice(index + 2)];
}

/**
 * One crosstab cell: the statistic, shaded by its benchmark band (or by
 * attainment of a standard), with the Mann-Kendall trend beneath it and a map
 * button when there is somewhere to go.
 *
 * Four rules this component exists to keep:
 *   - color is never the only carrier of the band; the band name is in the
 *     tooltip and in the screen-reader text
 *   - the trend is an icon alone, keyed by the legend. The word is dropped
 *     because it repeated the legend in every cell; the hidden sentence
 *     below still spells it out, so assistive tech is unaffected
 *   - what is clickable looks clickable: a named button, not a whole cell that
 *     happens to respond
 *   - that affordance is a real link, so it is reachable by keyboard and
 *     behaves like every other link in the browser, without the cell itself
 *     becoming a tab stop
 */
const ScorecardCell = React.memo(
  ({ cell, href, groupDivider = false, onOpenSeasons = null }) => {
    const classes = useStyles();
    const cellClass = `${classes.cell}${
      groupDivider ? ` ${classes.groupDivider}` : ""
    }`;

    if (!cell) return <TableCell className={cellClass} />;

    const background = cell.backgroundColor ?? "transparent";
    const color = cell.backgroundColor
      ? getTextColor(cell.backgroundColor)
      : undefined;

    const canOpenMap = Boolean(cell.hasData && href);
    const isDash =
      cell.kind === "never-sampled" || cell.kind === "not-applicable";

    const tooltip = (
      <>
        <Typography className={classes.tooltipTitle}>
          {cell.tooltip.title}
        </Typography>
        {cell.tooltip.lines.map((line) => {
          const [key, value] = splitTooltipLine(line);
          return (
            <Typography key={line} className={classes.tooltipLine}>
              {key ? (
                <>
                  <span className={classes.tooltipKey}>{key}:</span> {value}
                </>
              ) : (
                line
              )}
            </Typography>
          );
        })}
        {canOpenMap && (
          <Typography className={classes.tooltipHint}>
            {OPEN_IN_MAP_HINT}
          </Typography>
        )}
      </>
    );

    return (
      <Tooltip title={tooltip} arrow enterTouchDelay={0} leaveTouchDelay={6000}>
        <TableCell
          className={cellClass}
          style={{ backgroundColor: background, color }}
        >
          {/* The visible content is hidden from screen readers, which read the
            one plain sentence below instead of spelling out icon ligatures. */}
          <span aria-hidden="true">
            {cell.hasData ? (
              <>
                <span
                  className={`${classes.value}${
                    cell.isSmallSample ? ` ${classes.valueMuted}` : ""
                  }`}
                >
                  {cell.display}
                </span>
                <span className={classes.trendRow}>
                  <Icon className={classes.trendIcon}>{cell.trend.icon}</Icon>
                </span>
                {cell.isSmallSample && (
                  <span className={classes.smallSample}>
                    n = {cell.recordcount}
                  </span>
                )}
              </>
            ) : isDash ? (
              <span className={classes.dash}>{cell.display}</span>
            ) : (
              <span className={classes.noData}>n/a</span>
            )}
          </span>

          <span className={classes.srOnly}>{cell.srText}</span>

          {canOpenMap && (
            <IconButton
              className={`${classes.mapButton} ${NO_PRINT_CLASS}`}
              size="small"
              // A real anchor, not a button with a click handler: middle-click,
              // ctrl-click, "copy link address" and the hover status bar all
              // come free, and the browser owns the new tab.
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              // Names the destination and the subject, so a screen reader user
              // hears "Open CT-P1, Total Nitrogen as N in the water quality map"
              // rather than sixteen identical "link, map" stops. The new-tab
              // warning is part of the name because the context change is not
              // otherwise announced.
              aria-label={`Open ${cell.siteName}, ${cell.parameterName} in the water quality map (opens in a new tab)`}
            >
              <MapIcon className={classes.mapIcon} />
            </IconButton>
          )}

          {/* A reservoir standard cell: the season-by-season table lives
            behind this button, so the tooltip stays a tooltip. */}
          {Boolean(onOpenSeasons && cell.seasons && cell.seasons.length) && (
            <IconButton
              className={`${classes.mapButton} ${NO_PRINT_CLASS}`}
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onOpenSeasons(event, cell);
              }}
              aria-label={`${SEASON_DETAIL.buttonLabel} for ${cell.siteName}, ${cell.parameterName}`}
            >
              <EventNoteIcon className={classes.mapIcon} />
            </IconButton>
          )}
        </TableCell>
      </Tooltip>
    );
  }
);

ScorecardCell.displayName = "ScorecardCell";

export default ScorecardCell;
